<script setup lang="ts">
import SideModal from "~/components/Common/Modals/SideModal.vue";
import SessionRecap from "~/components/Pages/training-plan/session-sidemodal/SessionRecap.vue";
import SessionUserInfos from "~/components/Pages/training-plan/session-sidemodal/SessionUserInfos.vue";
import SessionHistory from "~/components/Pages/training-plan/session-sidemodal/SessionHistory.vue";
import SessionHeading from "~/components/Pages/training-plan/session-sidemodal/SessionHeading.vue";
import SessionAbsentStatusInfos from "~/components/Pages/training-plan/session-sidemodal/SessionAbsentStatusInfos.vue";
import SessionAttendeeAssignment from "~/components/Pages/training-plan/session-sidemodal/SessionAttendeeAssignment.vue";
import SessionSatisfactionsRedirect from "~/components/Pages/training-plan/session-sidemodal/SessionSatisfactionsRedirect.vue";
import SessionFileUpload from "~/components/Pages/training-plan/session-sidemodal/SessionFileUpload.vue";
import { useSessionIndividual } from "~/composables/sessions/useSessionIndividual";
import { ItemType } from "~/utils/enums/common.enums";
import { SessionLmsAction, SessionStatus } from "@simbelapp/order-sdk";
import { useSessionsStore } from "~/store/sessions/sessions-store";
import { useBookingsStore } from "~/store/bookings/bookings.store";
import SessionRemoveProjectUserModal from "~/components/Pages/training-plan/session-sidemodal/SessionRemoveProjectUserModal.vue";
import InviteModalConfirm from "./status-modal/InviteModalConfirm.vue";
import AbsentModalConfirm from "./status-modal/AbsentModalConfirm.vue";
import { storeToRefs } from "pinia";
import CancelModal from "./status-modal/CancelModal.vue";
import { SessionStatusEnum } from "~/utils/enums/session-details-enums";
import { useAuthStore } from "~/store/auth/auth.store";
import { useNotificationsStore } from "~/store/notifications/notifications.store";
import { componentAvalaible } from "~/utils/enums/notifications.enums";
import NotificationModalConfirm from "~/components/Pages/notifications/NotificationModalConfirm.vue";
import { useSessionDetailsStore } from "~/store/sessions/session-details-store";
import { usePagination } from "~/composables/paginations/usePagination";
import Tooltip from "~/components/Common/Tooltip.vue";
import SessionUserRequest from "./SessionUserRequest.vue";
import SessionUnscheduleAttendeeInfos from "./SessionUnscheduleAttendeeInfos.vue";
import SessionUnscheduleAttendeeUpdateModal from "~/components/Pages/training-plan/session-details/modals/session-edit-modal/SessionAttendees/SessionUnscheduleAttendeeUpdateModal.vue";
import { useSessionDetailsAttendeesStore } from "~/store/sessions/session-details-attendees-store";
import { useSessionDetailsDocumentsStore } from "~/store/sessions/session-details-documents-store";
import { PaginationKeysEnum } from "~/utils/enums/pagination-keys.enums";
import { useSessionLmsRules } from "~/composables/sessions/useSessionLmsRules";
import AppButton from "~/components/Common/Buttons/AppButton.vue";
import LoadButton from "~/components/Common/Buttons/LoadButton.vue";
import DateChoiceTrackingBlock from "~/components/Pages/date-choice/DateChoiceTrackingBlock.vue";
import DateChoiceDetailsActions from "~/components/Pages/date-choice/DateChoiceDetailsActions.vue";
import EditPendingRegistrationPriceModal from "~/components/Pages/date-choice/EditPendingRegistrationPriceModal.vue";
import RemoveAttendeeFlowModal from "~/components/Pages/training-plan/modals/RemoveAttendeeFlowModal.vue";
import { toProjectUserId, useDateChoiceStore } from "~/store/date-choice/date-choice.store";
import type { RemoveProjectUsersDtoRequestActionEnum } from "@simbelapp/order-sdk";

const sessionIndividual = useSessionIndividual();
const sessionStore = useSessionsStore();
const bookingsStore = useBookingsStore();
const usePaginate = usePagination();
const showRemoveAttendeeModal = ref(false);
const showConfirmSendNotification = ref(false);
const isSaving = ref(false);
const sessionDetailsStore = useSessionDetailsStore();
const { isLmsActionDisabled } = useSessionLmsRules();
const notificationStore = useNotificationsStore();
const { notifyModal, createSession } = storeToRefs(notificationStore);
const { choiceDelivery, content, subject, sendIcs } = componentAvalaible;
const sessionDetailsAttendeesStore = useSessionDetailsAttendeesStore();
const { updateUnscheduleAttendeeModal } = storeToRefs(sessionDetailsAttendeesStore);
const sessionDetailsDocumentsStore = useSessionDetailsDocumentsStore();
const paginationOptions = usePagination(PaginationKeysEnum.ATTENDEES_PAGINATION);
const paginationOptionsAttendees = paginationOptions.getFindOptions(paginationOptions.paginateOptions.value);

const authStore = useAuthStore();
const dateChoiceStore = useDateChoiceStore();

const userInfos = computed(() => sessionIndividual.getUserInfos());

// Session behind this inscription, for the "Enquêtes de satisfaction" redirect. Only a session-backed
// inscription has one — for a project, sourceId is the projectId and there is no Évaluations tab.
const evaluationsSessionId = computed(() =>
  sessionIndividual.sourceType.value === ItemType.SESSION ? sessionIndividual.sourceId.value : null,
);

const isPlanningInProgress = computed(() => {
  const details = sessionDetailsStore.sessionDetails;
  return !!(
    details?.sessionId &&
    (details.sessionStatus === SessionStatus.PlanningInProgress || details.itemType === ItemType.PROJECT)
  );
});

const { showSessionStatusActionModal, trainingPlanSessionIndividual, showUpdateSessionModal } =
  storeToRefs(sessionStore);

/**
 * A registration awaiting a session carries a `dateChoice` block and a `pr-<projectUserId>` id:
 * its details replace the "Étapes" block with "Choix de date" (AC-SUIVI.5).
 */
const dateChoice = computed(() => trainingPlanSessionIndividual.value?.dateChoice ?? null);

// The `dateChoice` block is the only reliable marker: the `pr-` prefix is carried by every project
// row, drafts included.
const pendingProjectUserId = computed(() =>
  dateChoice.value ? toProjectUserId(trainingPlanSessionIndividual.value?.id) : null,
);

/** A validated free price is 0, not "no price": only an absent or non-numeric value becomes null. */
const pendingPriceExclTax = computed(() => {
  const raw = trainingPlanSessionIndividual.value?.priceExclTax;
  if (raw === null || raw === undefined) return null;
  const price = Number(raw);
  return Number.isFinite(price) ? price : null;
});

function hideModal() {
  sessionStore.trainingPlanSessionIndividual = null;
  sessionStore.showSessionModal = false;
  sessionStore.sessions = null;
  dateChoiceStore.closeAllModals();
  bookingsStore.$reset();
}

function removeUser() {
  if (trainingPlanSessionIndividual.value?.id?.includes("tr-")) {
    showSessionStatusActionModal.value.canceled = true;
  } else {
    showRemoveAttendeeModal.value = true;
  }
}

/**
 * Deleting a pending registration: same contract as deleting a registration, hence the same
 * confirmation journey (including the fate of the linked request).
 */
async function removePendingRegistration(
  requestAction: RemoveProjectUsersDtoRequestActionEnum | null = null,
  requestComment?: string,
) {
  if (!pendingProjectUserId.value) return;

  const ok = await dateChoiceStore.removePendingRegistration(pendingProjectUserId.value, {
    requestAction,
    requestComment,
  });
  showRemoveAttendeeModal.value = false;
  if (ok) {
    hideModal();
    await usePaginate.fetchData();
  }
}

/** The row stays open after editing the price: only the summary changes. */
async function refreshPendingRegistration() {
  dateChoiceStore.showEditPriceModal = false;
  if (trainingPlanSessionIndividual.value?.id) {
    await sessionStore.fetchTrainingPlanSession(trainingPlanSessionIndividual.value.id);
  }
  await usePaginate.fetchData();
}

async function refreshDocs() {
  if (sessionDetailsStore.sessionDetails?.id) {
    await sessionDetailsDocumentsStore.fetchSessionDocumentsWithPagination(sessionDetailsStore.sessionDetails?.id, {
      limit: 10000,
    });
  }
  createSession.value.documents = sessionDetailsDocumentsStore.sessionDocumentsPagination?.data ?? [];
}

async function reset() {
  if (sessionDetailsStore.sessionDetails)
    await sessionDetailsAttendeesStore.fetchSessionAttendeesFullDatas(
      sessionDetailsStore.sessionDetails.id,
      sessionDetailsStore.sessionDetails.itemType,
      paginationOptionsAttendees,
    );
}

function openConfigNotif() {
  showConfirmSendNotification.value = false;
}

async function handleLater() {
  if (sessionDetailsStore.sessionDetails?.id && sessionDetailsStore.sessionDetails?.itemType) {
    await sessionDetailsAttendeesStore.fetchSessionAttendeesFullDatas(
      sessionDetailsStore.sessionDetails?.id,
      sessionDetailsStore.sessionDetails?.itemType,
      paginationOptionsAttendees,
    );
  }
  showConfirmSendNotification.value = false;
  hideModal();
}

async function notifConfirmModal() {
  if (trainingPlanSessionIndividual.value) {
    const id = trainingPlanSessionIndividual.value.id;

    await sessionStore.fetchTrainingPlanSession(id);
    if (!sessionDetailsStore.sessionViewModal) {
      await usePaginate.fetchData();
    } else {
      await sessionDetailsStore.fetchSessionDetails(
        sessionDetailsStore.sessionDetails?.sessionId!,
        sessionDetailsStore.sessionDetails?.itemType!,
      );
      await sessionDetailsAttendeesStore.fetchSessionAttendeesFullDatas(
        sessionDetailsStore.sessionDetails?.sessionId!,
        sessionDetailsStore.sessionDetails?.itemType!,
        paginationOptionsAttendees,
      );
    }
  }

  showSessionStatusActionModal.value.registered = false;
  showUpdateSessionModal.value = false;
  showConfirmSendNotification.value = true;
}

const isDisabled = computed(
  () => !userInfos.value.userId || isLmsActionDisabled(SessionLmsAction.RemoveAttendeeRecord),
);

async function saveAssignment() {
  const bookingId = trainingPlanSessionIndividual.value?.bookingId;
  if (!bookingId) {
    isSaving.value = false;
    return;
  }
  if (bookingsStore.tmpBookingLegalEntity?.companyId) {
    await bookingsStore.updateBookingLegalEntity(bookingId, bookingsStore.tmpBookingLegalEntity.companyId);
  }
  await bookingsStore.updateBookingUserGroups(
    bookingId,
    bookingsStore.tmpBookingGroups.map((g) => g.groupId),
  );
  isSaving.value = false;
  hideModal();
}
</script>

<template>
  <SideModal @hide="hideModal">
    <template #title> Détail inscription </template>
    <template #content>
      <div>
        <SessionHeading></SessionHeading>
        <!-- AC-SUIVI.10 ordering: identity, badge, actions, summary, date choice -->
        <DateChoiceDetailsActions
          v-if="dateChoice && pendingProjectUserId"
          :project-user-id="pendingProjectUserId"
          :training-id="trainingPlanSessionIndividual?.trainingId ?? null"
          :training="trainingPlanSessionIndividual?.training ?? null"
          :project-id="trainingPlanSessionIndividual?.projectId ?? null"
          :session-id="trainingPlanSessionIndividual?.sessionId ?? null"
          :allowed-actions="trainingPlanSessionIndividual?.allowedActions ?? []"
          :available-sessions-count="dateChoice.availableSessionsCount"
        />
        <SessionRecap></SessionRecap>
        <DateChoiceTrackingBlock v-if="dateChoice" :date-choice="dateChoice" />
        <SessionAbsentStatusInfos
          v-if="
            sessionIndividual.getSessionHeading().status === SessionStatusEnum.NO_SHOW ||
            trainingPlanSessionIndividual?.noShowDate
          "
        ></SessionAbsentStatusInfos>
        <SessionUnscheduleAttendeeInfos
          v-if="sessionIndividual.getSessionHeading().status === SessionStatusEnum.CANCELED"
        ></SessionUnscheduleAttendeeInfos>
        <SessionUserInfos
          v-if="sessionIndividual.getSessionHeading().status != SessionStatusEnum.PENDING_INVITATION"
        ></SessionUserInfos>
        <SessionAttendeeAssignment
          v-if="sessionIndividual.getSessionHeading().status != SessionStatusEnum.PENDING_INVITATION"
        ></SessionAttendeeAssignment>
        <SessionUserRequest></SessionUserRequest>
        <SessionHistory
          v-if="sessionIndividual.getSessionHeading().status != SessionStatusEnum.PENDING_INVITATION"
        ></SessionHistory>
        <SessionFileUpload v-if="sessionIndividual.sourceType.value === ItemType.SESSION"></SessionFileUpload>
        <SessionSatisfactionsRedirect
          v-if="sessionIndividual.getSessionHeading().status != SessionStatusEnum.PENDING_INVITATION"
          :session-id="evaluationsSessionId"
        />
      </div>
    </template>
    <template #footer>
      <div class="session_modal__buttons">
        <Tooltip
          v-if="isDisabled && (authStore.isGroupAdmin || authStore.isCompanyAdmin)"
          :position="'top-left'"
          append-to-body
          minified
          :force-position-top="3"
        >
          <template #img>
            <AppButton type="outlined" disabled label="Supprimer" icon="delete_trash" variant="error" class="w-full" />
          </template>
          <template #content>
            <div class="flex gap-[8px] w-[414px]">
              <img src="/icons/emojis/eyes.svg" alt="" :style="{ height: '16px', width: '16px' }" />
              <span v-if="!isLmsActionDisabled(SessionLmsAction.RemoveAttendeeRecord)" class="body_regular">
                Impossible de supprimer cette inscription car elle a déjà été facturée. Contactez le support pour
                régulariser la situation.
              </span>
              <span v-else class="body_regular">
                Cette action n'est pas disponible pour une session importée d'un LMS
              </span>
            </div>
          </template>
        </Tooltip>
        <Tooltip
          v-else-if="!authStore.isGroupAdmin && !authStore.isCompanyAdmin"
          :position="'top-left'"
          append-to-body
          minified
          :force-position-top="3"
        >
          <template #img>
            <AppButton type="outlined" disabled label="Supprimer" icon="delete_trash" variant="error" class="w-full" />
          </template>
          <template #content>
            <div class="flex gap-[8px] w-[414px]">
              <img src="/icons/emojis/eyes.svg" alt="" :style="{ height: '16px', width: '16px' }" />
              <div class="flex flex-col body_regular">
                <span> Impossible de supprimer cette inscription. </span>
                <span> Contactez votre équipe RH pour régulariser la situation. </span>
              </div>
            </div>
          </template>
        </Tooltip>
        <AppButton
          v-else-if="authStore.isGroupAdmin || authStore.isCompanyAdmin"
          type="outlined"
          label="Supprimer"
          icon="delete_trash"
          variant="error"
          class="w-full"
          @click="removeUser"
        />
        <LoadButton
          v-if="
            sessionIndividual.getSessionHeading().status != SessionStatusEnum.PENDING_INVITATION &&
            (authStore.isGroupAdmin || authStore.isCompanyAdmin)
          "
          v-model="isSaving"
          class="w-full"
          text-confirm="Enregistrer"
          name-icon="tick"
          @click="saveAssignment"
        />
      </div>
    </template>
  </SideModal>
  <!-- A pending registration has no carrier session: it has its own deletion endpoint -->
  <RemoveAttendeeFlowModal
    v-if="showRemoveAttendeeModal && dateChoice"
    :has-linked-request="trainingPlanSessionIndividual?.isLinkedToARequest || false"
    :linked-attendees-count="trainingPlanSessionIndividual?.isLinkedToARequest ? 1 : 0"
    :show-notify-step="false"
    :delete-consequences="[]"
    @hide="showRemoveAttendeeModal = false"
    @confirm-delete="removePendingRegistration()"
    @confirm-request="
      (action: RemoveProjectUsersDtoRequestActionEnum, comment?: string) => removePendingRegistration(action, comment)
    "
  />
  <SessionRemoveProjectUserModal
    v-else-if="showRemoveAttendeeModal"
    @hide="showRemoveAttendeeModal = false"
  ></SessionRemoveProjectUserModal>
  <EditPendingRegistrationPriceModal
    v-if="dateChoiceStore.showEditPriceModal && pendingProjectUserId"
    :project-user-id="pendingProjectUserId"
    :price-excl-tax="pendingPriceExclTax"
    @hide="dateChoiceStore.showEditPriceModal = false"
    @saved="refreshPendingRegistration"
  />
  <CancelModal v-if="showSessionStatusActionModal.canceled" @hide="showSessionStatusActionModal.canceled = false">
  </CancelModal>
  <InviteModalConfirm
    v-if="showSessionStatusActionModal.registered || showUpdateSessionModal"
    @hide="
      showSessionStatusActionModal.registered = false;
      showUpdateSessionModal = false;
    "
    @notifConfirmModal="notifConfirmModal"
  ></InviteModalConfirm>
  <AbsentModalConfirm v-if="showSessionStatusActionModal.no_show" @hide="showSessionStatusActionModal.no_show = false">
  </AbsentModalConfirm>
  <NotificationModalConfirm
    v-if="showConfirmSendNotification"
    :users="[userInfos]"
    :is-planning-in-progress="isPlanningInProgress"
    @openConfigNotif="openConfigNotif"
    @handleLater="handleLater"
  >
  </NotificationModalConfirm>
  <PagesNotificationsModal
    v-if="notifyModal && trainingPlanSessionIndividual?.sessionId"
    :store="createSession"
    :element-to-show="[choiceDelivery, content, subject, sendIcs]"
    :session-id="trainingPlanSessionIndividual.sessionId"
    @refresh-documents="refreshDocs"
    @reset="reset"
  >
  </PagesNotificationsModal>
  <SessionUnscheduleAttendeeUpdateModal
    v-if="updateUnscheduleAttendeeModal"
    @hide="updateUnscheduleAttendeeModal = false"
  ></SessionUnscheduleAttendeeUpdateModal>
</template>

<style lang="scss" scoped>
.session_modal__buttons {
  @apply grid gap-2 px-10 py-4;
  box-shadow: var(--shadow-soft);
}

.slide-fade-enter-active,
.slide-fade-leave-active {
  transition: all 0.5s;
}

.slide-fade-enter,
.slide-fade-leave-to {
  opacity: 0;
  transform: translateX(100%);
  /* Déplacez l'élément de la droite à gauche */
}
</style>
